import { type ReactNode } from 'react';
import type { Lang } from '../../_configs/types';
import type { CalcFdResponseSource } from '../../_tools/types/calculatorFd';
import { type FaarenPickupDeliveryOption } from '../../_tools/types/faaren';
import { type ArrayElement } from '../../_tools/utils';
import { type Car, type SelectOption } from '../../_types/Car';

export enum CalcFdType {
    AutoAbo = 'autoAbo',
    Financing = 'financing',
    Leasing = 'leasing',
}

export enum CalcFdGapInsurance {
    NONE = 'NONE',
    GAP = 'GAP',
    GAP_PLUS = 'GAP_PLUS',
}

export enum CalcFdPaymentProtectionInsurance {
    NONE = 'NONE',
    RSV = 'RSV',
    RSVPLUS = 'RSVPLUS',
}

export type CalcFdForm = {
    afterSubmit?: string;
    downPayment: number;
    duration: number;
    gapInsurance: CalcFdGapInsurance;
    mileage: number;
    lastRate: number;
    paymentProtectionInsurance: CalcFdPaymentProtectionInsurance;
};

export type CalcFdPrecheckerRequestData = {
    bdkRetailerId: string | undefined;
    duration: number;
    hptVersion: 'v1' | 'v2';
    language: Lang;
    privateLeasing: boolean;
    requestType: CalcFdType;
    vehicleId: number;
    webCalcDomain: string | undefined;
};

export type CalcFdPrecheckerResponseData = {
    /**
     * @name Source.
     * @description Describes the source of the calculation precheck.
     * @example 'bdk'
     */
    source: CalcFdResponseSource;
} & (
    | {
          /**
           * @name FD possible.
           * @description Indicates whether a vehicle can use a FD.
           * @example false
           */
          fdPossible: false;
          /**
           * @name FD possible - reason.
           * @description Describes the reason why a vehicle is not FD-capable.
           * @example 'The vehicle price is too low'
           */
          fdPossibleReason: string;
      }
    | {
          /**
           * @name FD possible.
           * @description Indicates whether a vehicle can use a FD.
           * @example true
           */
          fdPossible: true;
          /**
           * @name Options.
           * @description Includes all filter facets for the calculator.
           * @example {}
           */
          options: {
              /**
               * @name Duration.
               * @description Includes all duration facets.
               * @example []
               */
              duration: SelectOption[];
              /**
               * @name GAP insurance.
               * @description Includes all gapInsurance facets.
               * @example []
               */
              gapInsurance: { i: CalcFdGapInsurance; t: string }[];
              /**
               * @name Mileage.
               * @description Includes all mileage facets.
               * @example []
               */
              mileage?: SelectOption[];
              /**
               * @name Payment protection insurance.
               * @description Includes all paymentProtectionInsurance facets.
               * @example []
               */
              paymentProtectionInsurance: { i: CalcFdPaymentProtectionInsurance; t: string }[];
          };
      }
);

export type CalcFdProps = {
    calculatorButton: (fd: FinanceData, disabled: boolean) => ReactNode;
    car: Car<false>;
    contactButtonLarge: React.JSX.Element;
};

export type FinanceData = {
    annualPercentageRate: number;
    disclaimer: {
        additional: string;
        info: string;
        summary: string;
    };
    downPayment: number;
    duration: number;
    gapInsurance: CalcFdGapInsurance;
    gapInsuranceAmount: number;
    lastRate: number;
    mileage: number;
    monthlyRate: number;
    monthlyRateNet: number;
    netLoan: number;
    nominalInterestRate: number;
    // transfer: number,
    total: number;
    type: CalcFdType;
    valid: boolean;
} & (
    | {
          damage_participation: number;
          delivery_option: FaarenPickupDeliveryOption[];
          delivery_time_after_booking: number;
          extraMileageCosts: number;
          fixed_duration: boolean;
          immediately_available: boolean;
          minimum_age: number;
          min_license_duration: number;
          pick_up_option: FaarenPickupDeliveryOption[];
          type: CalcFdType.AutoAbo;
      }
    | {
          financeCalculationId?: string;
          paymentProtectionInsurance: CalcFdPaymentProtectionInsurance;
          paymentProtectionInsuranceAmount: number;
          privateFinancing: boolean;
          type: CalcFdType.Financing;
      }
    | {
          extraMileageCosts: number;
          lowMileageCompensation: number;
          privateLeasing: boolean;
          type: CalcFdType.Leasing;
      }
);

export type DefaultValue =
    | { type: CalcFdType.AutoAbo; obj: ArrayElement<Car<false>['autoAbo']['plans']> }
    | { type: CalcFdType.Financing; obj: Car<false>['finanzierung'] }
    | { type: CalcFdType.Leasing; obj: Car<false>[CalcFdType.Leasing] }
    | { type: 'leasingComm'; obj: Car<false>['leasingComm'] };

export type RateSettingsOptions = {
    duration: SelectOption[];
    gapInsurance: SelectOption[];
    depositRange: SelectOption[];
    lastRateRange: SelectOption[];
    mileage: SelectOption[];
    paymentProtectionInsurance: SelectOption[];
};
